import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronDown, ChevronRight, Code } from "lucide-react";
import type { FpMessagePart } from "../../../../../schemas/codegen";

interface ToolCallPartProps {
  part: Extract<FpMessagePart, { type: "tool-call" }>;
  isExpanded: boolean;
  onToggleExpansion: (partId: string) => void;
}

export function ToolCallPart({
  part,
  isExpanded,
  onToggleExpansion,
}: ToolCallPartProps) {
  if (!part?.id) {
    console.error("[ToolCallPart] part has no id", { part });
    return null;
  }

  return (
    <div className="ml-8 mr-2">
      {part.type === "tool-call" ? (
        // Tool call part rendering
        <Card className="bg-muted/50 border-l-4 border-l-primary">
          <CardContent className="p-2">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Code className="w-4 h-4 text-primary" />
                <span className="font-mono text-xs font-medium">
                  {part.toolName}
                </span>
                <Badge
                  variant={
                    part.status === "complete" || part.status === "executing"
                      ? "default"
                      : part.status === "error"
                        ? "destructive"
                        : "secondary"
                  }
                  className="text-xs"
                >
                  {part.status || "pending"}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onToggleExpansion(part.id)}
                >
                  {isExpanded ? (
                    <ChevronDown className="w-3 h-3" />
                  ) : (
                    <ChevronRight className="w-3 h-3" />
                  )}
                </Button>
              </div>
            </div>

            {isExpanded && (
              <div className="space-y-1">
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">
                    Arguments:
                  </p>
                  <pre className="text-xs bg-background p-1 rounded border">
                    {part.args ? JSON.stringify(part.args, null, 2) : "None"}
                  </pre>
                </div>

                {!!part.result && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">
                      Result:
                    </p>
                    <pre className="text-xs bg-background p-1 rounded border max-h-24 overflow-y-auto">
                      {typeof part.result === "string"
                        ? part.result
                        : JSON.stringify(part.result, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        // Unknown part type
        <Card className="bg-muted/50 border-l-4 border-l-orange-500">
          <CardContent className="p-2">
            <div className="flex items-center gap-2 mb-2">
              <Code className="w-4 h-4 text-orange-500" />
              <span className="font-mono text-sm font-medium text-orange-600">
                Unknown part type: {part.type}
              </span>
            </div>
            <pre className="text-xs bg-background p-2 rounded border">
              {JSON.stringify(part, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
